// FI-2173 quiet pull for the erm pages (Event Board, Event Log). the host
// never shows a loading state for these passes: it merges what comes back
// into what is on screen. this mixin only decides WHEN a pass runs.
//
// triggers, all coalesced into one timer:
//   tick     every 60s while the tab is visible (no-op while hidden)
//   visible  ~1s after the tab comes back (visibilitychange + window focus
//            both fire on a tab switch, so they share the timer), with a
//            10s floor since the last pass so a flurry of alt-tabs costs one
//   signal   ~2s after an 'erm-changed' ping on the $root bus for this
//            tenant (App.vue forwards the FI-1657 websocket notification;
//            the delay covers the gsi lag behind the write that raised it)
//   force    a pass the host asks for itself (a rejected drop, once the
//            red pulse has been seen)
//
// failure: the pass is swallowed and the effective interval doubles
// (120s, 240s, cap 5 min) until a pass succeeds or the tab comes back.
// a 403 halts polling outright rather than bouncing the reader to login
// (the pass runs with noAuthRedirect); the host's next full load calls
// liveRefreshReset() and polling resumes.
//
// host contract: canSoftRefresh() -> bool, softRefresh() -> promise, and a
// `tenant` computed the signal is matched against.

export const LIVE_REFRESH_MS = 60000
export const LIVE_REFRESH_MAX_MS = 300000
export const VISIBLE_SETTLE_MS = 1000
export const VISIBLE_FLOOR_MS = 10000
export const SIGNAL_DELAY_MS = 2000
// a tick may land a few ms before the interval has fully elapsed (the
// browser schedules repeats from the previous scheduled fire, not from
// our Date.now() read); a second of slack keeps the cadence at 60s
export const TICK_SLACK_MS = 1000

// pure: is a pass due right now? `now` is injected so specs need no fake
// timers. the tick honours the interval (or the backoff when larger); a
// return to the tab honours only the floor; a signal or a forced pass is
// due whenever the tab is visible. the tick gets TICK_SLACK_MS of slack
// against setInterval jitter.
export function liveRefreshDecision({
  now, lastPassAt = 0, intervalMs = LIVE_REFRESH_MS, hidden = false,
  backoffMs = 0, floorMs = VISIBLE_FLOOR_MS, halted = false, trigger = 'tick',
}) {
  if (hidden || halted) return false
  if (trigger === 'signal' || trigger === 'force') return true
  const age = now - (lastPassAt || 0)
  if (trigger === 'visible') return age >= floorMs
  return age + TICK_SLACK_MS >= Math.max(intervalMs, backoffMs)
}

// pure: the interval after one more failure. 60s -> 120s -> 240s -> 300s
export function nextBackoffMs(backoffMs, intervalMs = LIVE_REFRESH_MS, maxMs = LIVE_REFRESH_MAX_MS) {
  return Math.min(Math.max(backoffMs || 0, intervalMs) * 2, maxMs)
}

// pure: the tenant an erm websocket signal is about, or null when the
// payload is not an erm link. links are '/ops_desk?tenant=..&event_id=..'
// (ops_desk/notifications.py _EVENT_PATH_TEMPLATE)
export function ermSignalTenant(payload) {
  const link = payload && typeof payload.link === 'string' ? payload.link : ''
  if (!link.startsWith('/ops_desk?')) return null
  const m = /[?&]tenant=([^&]*)/.exec(link)
  if (m) return decodeURIComponent(m[1])
  return payload.tenant != null ? String(payload.tenant) : null
}

// a 403 reaching the soft pass means the session is gone; the pass asked
// execute() not to redirect, so it is our job to stop asking
export function isForbidden(e) {
  const r = e && e.response
  if (!r) return false
  if (r.status === 403) return true
  const d = r.data
  return !!(d && String(d.status_code) === '403')
}

// stronger triggers win when two arm the shared timer before it fires
const TRIGGER_RANK = { tick: 0, visible: 1, signal: 2, force: 3 }

export default {
  mounted() {
    this._live = { lastPassAt: 0, backoffMs: 0, halted: false, inFlight: false, pending: null }
    this._liveTimer = setInterval(() => this.liveRefreshTick('tick'), LIVE_REFRESH_MS)
    this._onLiveVisible = () => {
      if (typeof document !== 'undefined' && document.hidden) return
      this.liveRefreshArm('visible', VISIBLE_SETTLE_MS)
    }
    document.addEventListener('visibilitychange', this._onLiveVisible)
    window.addEventListener('focus', this._onLiveVisible)
    this._onErmChanged = payload => this.onErmChanged(payload)
    this.$root.$on('erm-changed', this._onErmChanged)
  },
  beforeDestroy() {
    clearInterval(this._liveTimer)
    clearTimeout(this._liveArmTimer)
    this._liveArmTimer = null
    document.removeEventListener('visibilitychange', this._onLiveVisible)
    window.removeEventListener('focus', this._onLiveVisible)
    this.$root.$off('erm-changed', this._onErmChanged)
  },
  methods: {
    // one shared timer: a second arm before it fires replaces the first,
    // keeping the stronger trigger, so a tab switch (visibilitychange plus
    // focus) or a burst of signals is one pass
    liveRefreshArm(trigger, delayMs) {
      const pending = this._live && this._live.pending
      const next = pending && TRIGGER_RANK[pending] > TRIGGER_RANK[trigger] ? pending : trigger
      clearTimeout(this._liveArmTimer)
      this._live.pending = next
      this._liveArmTimer = setTimeout(() => {
        this._liveArmTimer = null
        this._live.pending = null
        this.liveRefreshTick(next)
      }, delayMs)
    },
    onErmChanged(payload) {
      const t = ermSignalTenant(payload)
      if (t == null || String(t) !== String(this.tenant)) return
      this.liveRefreshArm('signal', SIGNAL_DELAY_MS)
    },
    // the host's full loads call this: a fresh dataset means a fresh start
    // for the backoff, and a 403 halt ends on the next user action
    liveRefreshReset() {
      if (!this._live) return
      this._live.backoffMs = 0
      this._live.halted = false
    },
    liveRefreshTick(trigger = 'tick') {
      const live = this._live
      if (!live || live.inFlight) return
      // coming back to the tab is a fresh look: forget the backoff
      if (trigger === 'visible') live.backoffMs = 0
      const due = liveRefreshDecision({
        now: Date.now(),
        lastPassAt: live.lastPassAt,
        hidden: typeof document !== 'undefined' && document.hidden,
        backoffMs: live.backoffMs,
        halted: live.halted,
        trigger,
      })
      if (!due) return
      if (typeof this.canSoftRefresh !== 'function' || !this.canSoftRefresh()) return
      return this._liveRun()
    },
    async _liveRun() {
      const live = this._live
      live.inFlight = true
      live.lastPassAt = Date.now()
      try {
        await this.softRefresh()
        live.backoffMs = 0
      } catch (e) {
        if (isForbidden(e)) {
          live.halted = true
        } else {
          live.backoffMs = nextBackoffMs(live.backoffMs)
        }
        // eslint-disable-next-line no-console
        console.warn('erm live refresh failed', e)
      } finally {
        live.inFlight = false
      }
    },
  },
}
